import { z } from "zod";

import {
  ExtractAtomicOperationFromDefinition,
  createAtomicOperationDefinition,
} from "../../atomic-operations/index.js";
import { keyValueUpdatePayloadSchema } from "../../atomic-operations/keyValueUpdateSchema.js";
import { ExploreSpecSessionStateId } from "../../idTypeBrands.js";
import { SemanticCap } from "../../semanticCaps.js";

import {
  EXPLORE_SPEC_SESSION_STATE_FIELDS,
  ExploreSpecSessionStateUpdatableFields,
} from "./commonExploreSpecSessionStateTypes.js";
import { CREATE_EXPLORE_SPEC_SESSION_STATE } from "./createExploreSpecSessionState.js";

const UPDATE_EXPLORE_SPEC_SESSION_STATE_TYPE =
  "UPDATE_EXPLORE_SPEC_SESSION_STATE" as const;

export const UpdateExploreSpecSessionStatePayload = keyValueUpdatePayloadSchema(
  {
    shared: {
      exploreSpecSessionStateId: ExploreSpecSessionStateId,
      shouldRunCell: z.boolean().optional(),
    },
    fields: EXPLORE_SPEC_SESSION_STATE_FIELDS,
  },
);
export type UpdateExploreSpecSessionStatePayload = z.infer<
  typeof UpdateExploreSpecSessionStatePayload
>;

export const UPDATE_EXPLORE_SPEC_SESSION_STATE =
  createAtomicOperationDefinition({
    type: UPDATE_EXPLORE_SPEC_SESSION_STATE_TYPE,
    payloadSchema: UpdateExploreSpecSessionStatePayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_APP_SESSION,
    },
    writeAuth: {
      kind: "hasSemanticCapOnIdArg",
      cap: SemanticCap.EDIT_APP_SESSION,
      idArg: "exploreSpecSessionStateId",
      idType: "ExploreSpecSessionState",
    },
    conflictId: (op) =>
      `${UPDATE_EXPLORE_SPEC_SESSION_STATE_TYPE}-${op.payload.exploreSpecSessionStateId}-${op.payload.key}`,
    creationId: (op) =>
      `${CREATE_EXPLORE_SPEC_SESSION_STATE.type}-${op.payload.exploreSpecSessionStateId}`,
    create: <K extends keyof ExploreSpecSessionStateUpdatableFields>(args: {
      exploreSpecSessionStateId: ExploreSpecSessionStateId;
      key: K;
      value: ExploreSpecSessionStateUpdatableFields[K];
      shouldRunCell?: boolean;
    }) => ({
      type: UPDATE_EXPLORE_SPEC_SESSION_STATE_TYPE,
      payload: {
        ...args,
      },
    }),
    logSafe: ["exploreSpecSessionStateId", "key", "shouldRunCell"],
  });

export type UPDATE_EXPLORE_SPEC_SESSION_STATE =
  ExtractAtomicOperationFromDefinition<
    typeof UPDATE_EXPLORE_SPEC_SESSION_STATE
  >;
